import { useState } from "react"
import { BookOpenIcon, ChevronDownIcon } from "@heroicons/react/16/solid"
import { ModuleGroup } from "../hooks/useCourseLessonMap"
import { LessonFilter } from "./LessonFilterBar"

type Props = {
  modules: ModuleGroup[]
  value: LessonFilter
  onChange: (f: LessonFilter) => void
}

/** Persistent side list of modules/lessons for filtering chat by lesson — an alternative to the dropdown LessonFilterBar. */
export const LessonSidebar = ({ modules, value, onChange }: Props) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggle = (id: string) => setCollapsed((p) => ({ ...p, [id]: !p[id] }))

  return (
    <div className="space-y-0.5">
      <p className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Уроки</p>

      <button
        type="button"
        onClick={() => onChange({ type: "all" })}
        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors ${
          value.type === "all" ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-600 hover:bg-gray-50"
        }`}
      >
        <BookOpenIcon className="w-4 h-4 flex-shrink-0" />
        Все сообщения
      </button>

      {modules.map((m) => {
        const isCollapsed = !!collapsed[m.moduleId]
        const moduleActive = value.type === "module" && value.id === m.moduleId

        return (
          <div key={m.moduleId} className="pt-1.5">
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => toggle(m.moduleId)}
                className="p-1 text-gray-300 hover:text-gray-500 flex-shrink-0"
              >
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
              </button>
              <button
                type="button"
                onClick={() => onChange({ type: "module", id: m.moduleId })}
                className={`flex-1 min-w-0 text-left px-1.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider truncate transition-colors ${
                  moduleActive ? "text-blue-600" : "text-gray-400 hover:text-gray-600"
                }`}
                title={m.title}
              >
                {m.title}
              </button>
            </div>

            {!isCollapsed && (
              <div className="ml-4 border-l border-gray-100 pl-2 space-y-0.5 mt-0.5">
                {m.lessons.map((l) => {
                  const active = value.type === "lesson" && value.id === l.id
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => onChange({ type: "lesson", id: l.id })}
                      title={l.title}
                      className={`w-full text-left px-2 py-1.5 rounded-lg text-sm truncate transition-colors ${
                        active ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {l.title}
                    </button>
                  )
                })}
                {m.lessons.length === 0 && (
                  <p className="px-2 py-1 text-xs text-gray-300 italic">Нет уроков</p>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
